/**
 * DemoReceiptModal — демо сканирования чека для непремиум пользователей
 * Соответствует mobile DemoReceiptModal по структуре и стилю
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { PremiumUpgradeModal } from './PremiumFeature/PremiumUpgradeModal';
import { getDemoReceiptScan } from '../utils/premiumDemoData';
import './DemoReceiptModal.css';

interface DemoReceiptModalProps {
  visible: boolean;
  onClose: () => void;
  onRequestPremium?: () => void;
}

export function DemoReceiptModal({ visible, onClose, onRequestPremium }: DemoReceiptModalProps) {
  const { t } = useTranslation();
  const demoData = getDemoReceiptScan();
  const { store, date, items, total, currencySymbol } = demoData;

  const [step, setStep] = useState<'scanning' | 'result'>('scanning');
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const handlePremiumClick = () => {
    if (onRequestPremium) {
      onClose();
      onRequestPremium();
    } else {
      setShowPremiumModal(true);
    }
  };

  useEffect(() => {
    if (!visible) return;
    const t1 = setTimeout(() => setStep('result'), 2500);
    return () => clearTimeout(t1);
  }, [visible]);

  useEffect(() => {
    if (!visible) setStep('scanning');
  }, [visible]);

  const formatPrice = (price: number) =>
    currencySymbol === '$' || currencySymbol === '€'
      ? `${price.toFixed(2)} ${currencySymbol}`
      : `${price.toLocaleString()} ${currencySymbol}`;

  if (!visible) return null;

  return createPortal(
    <>
      <div className="demo-modal-overlay" onClick={onClose}>
        <div className="demo-modal demo-receipt-modal" onClick={(e) => e.stopPropagation()}>
          <button className="demo-modal__close" onClick={onClose} aria-label={t('common.close')}>
            ×
          </button>
          <h2 className="demo-modal__title">{t('receiptScanner.title')}</h2>

          <div className="demo-modal__content">
            {step === 'scanning' && (
              <div className="demo-receipt-scanning">
                <div className="demo-receipt-preview">
                  <Icon name="file-text" size={80} color="var(--color-text-secondary)" />
                  <div className="demo-receipt-scanline" />
                </div>
                <p className="demo-receipt-scanning-text">{t('receiptScanner.scanning')}</p>
                <p className="demo-receipt-scanning-hint">{t('receipt.scanningHint')}</p>
              </div>
            )}

            {step === 'result' && (
              <div className="demo-receipt-result">
                <div className="demo-receipt-store">
                  <Icon name="building" size={24} color="#34C759" />
                  <div>
                    <p className="demo-receipt-store-name">{store}</p>
                    <p className="demo-receipt-store-date">{date}</p>
                  </div>
                  <div className="demo-receipt-success-badge">
                    <Icon name="check" size={14} color="#FFF" />
                  </div>
                </div>
                <div className="demo-receipt-items">
                  {items.map((item, i) => (
                    <div key={i} className="demo-receipt-item">
                      <div className="demo-receipt-item-info">
                        <span>{item.name}</span>
                        {item.quantity > 1 && <span className="demo-receipt-qty">×{item.quantity}</span>}
                      </div>
                      <span>{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
                <div className="demo-receipt-total">
                  <span>{t('transaction.total')}:</span>
                  <span className="demo-receipt-total-amount">{formatPrice(total)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="demo-modal__badge">{t('premium.demoMode')}</div>

          <button className="demo-modal__premium-btn" onClick={handlePremiumClick}>
            <Icon name="star" size={20} color="#FFFFFF" />
            {t('premium.unlockFeature')}
            <Icon name="chevron-right" size={20} color="#FFFFFF" />
          </button>
        </div>
      </div>

      {!onRequestPremium && (
        <PremiumUpgradeModal
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          featureId="receipt_scanner"
          onUpgrade={() => {
            setShowPremiumModal(false);
            onClose();
          }}
        />
      )}
    </>,
    document.body
  );
}
